import { useEffect, useRef, useState } from 'react'
import type { Phone } from '../types'
import { TIMEOUTS } from '../config/constants'
import { copyToClipboard } from '../lib/clipboard'

export interface PhoneActionsCallbacks {
  onEdit: (phone: Phone) => void
  onDelete: (phone: Phone) => void
  onToggleActive: (phone: Phone) => void
  /** Admin-only — frees the phone back to the orphan pool. Hidden when
   *  phone.ownerKind !== 'admin' or callback not provided. */
  onDetachFromAdmin?: (phone: Phone) => void
}

interface PhoneActionsProps extends PhoneActionsCallbacks {
  phone: Phone
}

/**
 * Per-phone action bar: edit, copy-API-token (when present),
 * toggle-active, detach-from-admin (admin-owned only), delete. Same
 * surface that lived inline in PhoneCard.tsx — extracted so PhoneRow
 * and PhoneMobileCard can share without duplicating SVG icons.
 */
export function PhoneActions({
  phone,
  onEdit,
  onDelete,
  onToggleActive,
  onDetachFromAdmin,
}: PhoneActionsProps) {
  const [copied, setCopied] = useState(false)
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (copyResetTimer.current !== null) clearTimeout(copyResetTimer.current)
  }, [])

  const copyToken = async () => {
    if (!phone.apiToken) return
    if (await copyToClipboard(phone.apiToken)) {
      setCopied(true)
      if (copyResetTimer.current !== null) clearTimeout(copyResetTimer.current)
      copyResetTimer.current = setTimeout(
        () => setCopied(false),
        TIMEOUTS.CLIPBOARD_FEEDBACK_MS,
      )
    }
  }

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onEdit(phone)}
        title="Edit"
        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </button>

      {phone.apiToken && (
        <button
          onClick={copyToken}
          title="Copy API Token"
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          {copied ? (
            <span className="text-xs font-medium text-green-500">Copied!</span>
          ) : (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          )}
        </button>
      )}

      <button
        onClick={() => onToggleActive(phone)}
        title={phone.isActive ? 'Deactivate' : 'Activate'}
        className={`p-1.5 transition-colors ${
          phone.isActive
            ? 'text-gray-400 hover:text-amber-500'
            : 'text-amber-500 hover:text-green-500'
        }`}
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
          <line x1="12" y1="2" x2="12" y2="12" />
        </svg>
      </button>

      {phone.ownerKind === 'admin' && onDetachFromAdmin && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDetachFromAdmin(phone)
          }}
          title="Отвязать от админа — вернёт трубку в пул свободных"
          className="text-xs font-medium px-2 py-1 rounded border border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors"
        >
          Отвязать от админа
        </button>
      )}

      <button
        onClick={() => onDelete(phone)}
        title="Delete"
        className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>
    </div>
  )
}
